/**
 * NymoArt Coffee Info - JavaScript v2.0.0
 * 
 * Custom Element for displaying coffee batch information
 * Listens to nymo:code:detected events and loads coffee data
 * 
 * @requires NymoArt (nymo-base.js)
 */

if (!customElements.get('nymo-coffee-info')) {
  customElements.define('nymo-coffee-info', class NymoCoffeeInfo extends HTMLElement {
    
    constructor() {
      super();
      
      this.moduleId = this.dataset.moduleId || NymoArt.utils.uniqueId('coffee-');
      
      // State
      this.state = {
        currentBatchId: null,
        abortController: null
      };
      
      // Element references
      this.elements = {};
      
      // Bound event handlers
      this._onCodeDetected = this._handleCodeDetected.bind(this);
      this._onCodeReset = this._handleCodeReset.bind(this);
    }
    
    connectedCallback() {
      // Get element references
      this.elements = {
        loading: this.querySelector('[data-loading]'),
        error: this.querySelector('[data-error]'),
        content: this.querySelector('[data-content]'),
        batchId: this.querySelector('[data-batch-id]'),
        roastDate: this.querySelector('[data-roast-date]'),
        fields: {}
      };
      
      // Get all data-field elements
      this.querySelectorAll('[data-field]').forEach(el => {
        this.elements.fields[el.dataset.field] = el;
      });
      
      // Subscribe to events
      this._unsubscribeDetected = NymoArt.events.on('nymo:code:detected', this._onCodeDetected);
      this._unsubscribeReset = NymoArt.events.on('nymo:code:reset', this._onCodeReset);
      
      // Also listen to legacy DOM events for backwards compatibility
      document.addEventListener('qrCodeDetected', this._onLegacyCodeDetected = (e) => {
        this._handleCodeDetected({ designId: e.detail.designId, imageData: null });
      });
      document.addEventListener('qrScannerReset', this._onLegacyReset = () => {
        this._handleCodeReset();
      });
      
      // Register module
      NymoArt.modules.register(`coffee-info-${this.moduleId}`, this);
      
      NymoArt.log.info(`Coffee Info initialized: ${this.moduleId}`);
    }
    
    disconnectedCallback() {
      // Cleanup subscriptions
      if (this._unsubscribeDetected) this._unsubscribeDetected();
      if (this._unsubscribeReset) this._unsubscribeReset();
      
      // Remove legacy listeners
      if (this._onLegacyCodeDetected) {
        document.removeEventListener('qrCodeDetected', this._onLegacyCodeDetected);
      }
      if (this._onLegacyReset) {
        document.removeEventListener('qrScannerReset', this._onLegacyReset);
      }
      
      // Cancel any pending requests
      if (this.state.abortController) {
        this.state.abortController.abort();
      }
      
      NymoArt.modules.unregister(`coffee-info-${this.moduleId}`);
    }
    
    _handleCodeDetected(data) {
      const { designId, imageData } = data;
      NymoArt.log.info(`Coffee Info: Code detected - ${designId}`);
      
      // Check if we already have coffeeBatchId from imageData (optimization)
      const coffeeBatchId = imageData?.coffeeBatchId;
      
      if (coffeeBatchId) {
        // Direct load - no additional image query needed!
        this.loadCoffeeBatch(coffeeBatchId);
      } else {
        // Need to fetch image data first to get coffeeBatchId
        this.loadCoffeeInfoForDesign(designId);
      }
    }
    
    _handleCodeReset() {
      NymoArt.log.info('Coffee Info: Reset');
      this.hide();
    }
    
    /**
     * Load coffee info by first fetching design to get coffeeBatchId
     * @param {string} designId - Design ID
     */
    async loadCoffeeInfoForDesign(designId) {
      // Cancel previous request
      if (this.state.abortController) {
        this.state.abortController.abort();
      }
      this.state.abortController = new AbortController();
      
      // Show loading
      this.show();
      this._showLoading();
      
      try {
        // Get image data to find coffeeBatchId
        const imageData = await NymoArt.firebase.getDoc(
          'image',
          designId,
          this.state.abortController.signal
        );
        
        const coffeeBatchId = imageData.coffeeBatchId;
        
        if (!coffeeBatchId) {
          NymoArt.log.info(`No coffee batch for design: ${designId}`);
          this.hide(); // Graceful hide instead of error
          return;
        }
        
        // Load the coffee batch
        await this._loadBatchData(coffeeBatchId);
        
      } catch (error) {
        if (error.name === 'AbortError') {
          NymoArt.log.info('Coffee info request cancelled');
          return;
        }
        
        NymoArt.log.warn('Coffee info loading failed:', error);
        this.hide(); // Graceful hide
      }
    }
    
    /**
     * Load coffee batch directly by ID (optimized path)
     * @param {string} batchId - Coffee batch ID
     */
    async loadCoffeeBatch(batchId) {
      // Cancel previous request
      if (this.state.abortController) {
        this.state.abortController.abort();
      }
      this.state.abortController = new AbortController();
      
      // Show loading
      this.show();
      this._showLoading();
      
      try {
        await this._loadBatchData(batchId);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        
        NymoArt.log.error('Coffee batch loading failed:', error);
        this._showError(error.message);
      }
    }
    
    async _loadBatchData(batchId) {
      NymoArt.log.info(`Loading coffee batch: ${batchId}`);
      
      const batchData = await NymoArt.firebase.getDoc(
        'coffeeBatch',
        batchId,
        this.state.abortController.signal
      );
      
      this.state.currentBatchId = batchId;
      
      // Display the data
      this._displayBatchData(batchId, batchData);
      
      // Emit event
      NymoArt.events.emit('nymo:coffee:loaded', {
        batchId,
        batchData
      });
    }
    
    _displayBatchData(batchId, data) {
      // Update batch ID
      if (this.elements.batchId) {
        this.elements.batchId.textContent = `#${batchId}`;
      }
      
      // Format and display roast date
      if (this.elements.roastDate && data.roastDate) {
        const formattedDate = NymoArt.utils.formatDate(data.roastDate);
        this.elements.roastDate.textContent = `Geröstet am ${formattedDate}`;
      }
      
      // Update all field elements
      const fieldMappings = {
        aroma: data.aroma,
        beanComposition: data.beanComposition,
        roastLevel: this._formatRating(data.roastLevel, 5),
        intensity: this._formatRating(data.intensity, 5),
        acidity: this._formatRating(data.acidity, 5),
        brewMethods: data.brewMethods,
        recommendedAs: data.recommendedAs,
        origin: data.origin,
        altitude: data.altitude,
        processing: data.processing,
        roastMethod: data.roastMethod,
        certifications: data.certifications
      };
      
      for (const [field, value] of Object.entries(fieldMappings)) {
        if (this.elements.fields[field]) {
          this.elements.fields[field].textContent = value || '-';
        }
      }
      
      // Show content
      this._showContent();
      
      NymoArt.log.info(`Coffee info displayed for batch: ${batchId}`);
    }
    
    _formatRating(value, max = 5) {
      if (value === null || value === undefined) return '-';
      return `${value}/${max}`;
    }
    
    _showLoading() {
      this.elements.loading.hidden = false;
      this.elements.error.hidden = true;
      this.elements.content.hidden = true;
    }
    
    _showError(message) {
      this.elements.loading.hidden = true;
      this.elements.error.hidden = false;
      this.elements.error.textContent = `❌ ${message}`;
      this.elements.content.hidden = true;
    }
    
    _showContent() {
      this.elements.loading.hidden = true;
      this.elements.error.hidden = true;
      this.elements.content.hidden = false;
    }
    
    show() {
      this.hidden = false;
    }
    
    hide() {
      this.hidden = true;
      this.state.currentBatchId = null;
      
      // Cancel any pending requests
      if (this.state.abortController) {
        this.state.abortController.abort();
        this.state.abortController = null;
      }
    }
  });
  
  console.log('☕ NymoArt Coffee Info v2.0.0 loaded');
}

